import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { useLanguage } from "@/i18n";
import { PROJECTS } from "@/data/projects";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", venture: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", venture: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-bone/25 focus:border-ochre outline-none py-3 text-sm font-light text-bone placeholder:text-bone/35 transition-colors duration-300";

  return (
    <section id="contact" className="relative bg-ebony py-24 md:py-32 overflow-hidden" data-testid="contact-section">
      <div className="absolute inset-0 pattern-kitenge" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-12 gap-12 md:gap-20">
        <Reveal className="md:col-span-5">
          <p className="text-xs uppercase tracking-[0.3em] text-ochre mb-4">{t.contact.overline}</p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-bone" data-testid="contact-title">
            {t.contact.title}
          </h2>
          <p className="mt-6 text-sm md:text-base font-light leading-relaxed text-bone/60 max-w-md">{t.contact.sub}</p>
        </Reveal>
        <Reveal className="md:col-span-7" delay={0.1}>
          <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-8">
              <input required value={form.name} onChange={set("name")} placeholder={t.contact.name} className={inputCls} data-testid="contact-name-input" />
              <input required type="email" value={form.email} onChange={set("email")} placeholder={t.contact.email} className={inputCls} data-testid="contact-email-input" />
            </div>
            <select value={form.venture} onChange={set("venture")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="contact-venture-select">
              <option value="" className="text-ebony">{t.contact.ventureGeneral}</option>
              {PROJECTS.map((p) => (
                <option key={p.slug} value={p.name} className="text-ebony">
                  {p.name}
                </option>
              ))}
            </select>
            <textarea required rows={4} value={form.message} onChange={set("message")} placeholder={t.contact.message} className={`${inputCls} resize-none`} data-testid="contact-message-input" />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="group inline-flex items-center gap-3 rounded-full bg-terracotta px-8 py-4 text-sm font-medium text-bone hover:bg-ochre hover:text-ebony transition-colors duration-500 disabled:opacity-60"
            >
              {sending ? t.contact.sending : t.contact.send}
              <Send size={15} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
